import type { DataSourceFlow } from 'components/Emulator/utils/types';

export type ErrorItem = {
  field?: string;
  message?: string;
};

export type BackendErrorResponse = {
  error?: {
    code?: string;
    extra?: {
      'invalid-params'?: ErrorItem[];
    };
    message?: string;
  };
};

export type DjangoErrorResponse = {
  code: string;
  detail: string;
};

export type SuggestionSuccessRate = 'high' | 'low' | 'unsupported' | null;

export type TruvSuggestionType = {
  name: string;
  id?: string;
  domain: string;
  logo_url: string;
  company_mapping_id: string;
  confidence_level: string;
  sso?: {
    name: string;
  };
  success_rate: SuggestionSuccessRate;
};

export type BillingLinkResponse = {
  url: string;
};

export type BrandingResponse = {
  client_name: null | string;
  color_primary: null | string;
  color_background: null | string;
  logo: null | string;
  client_customer_support_email: null | string;
  client_user_agreement_link: null | string;
  client_user_agreement_link_title: null | string;
  is_white_background_enabled: boolean;
  is_confetti_hidden: boolean;
  is_get_help_hidden: boolean;
  is_full_view_enabled: boolean;
  is_before_you_go_screen_hidden: boolean;
  is_citadel_logo_hidden: boolean;
  how_it_works: null | 'link' | 'full_screen';
  consent_screen: null | 'link' | 'full_screen';
};

export type CompanyType = 'client' | 'employer' | 'individual' | 'self_serve';

export type CompanyResponse = {
  billing_email: string;
  client_id: string;
  company_type: CompanyType;
  has_prod_usage: boolean;
  name: string;
  parent_company_name: string;
  session_timeout: number | null;
  address: string;
  alerts_email: string;
  city: string;
  legal_name: string;
  show_prod_keys_to_developers: boolean;
  state: string;
  website: string;
  zip_code: string;
  verification_volume: VerificationVolume | null;
  // this field includes feature flags to enable features for the company that require logic on both BE and FE
  custom_flags: Record<string, unknown> | null;
};

export type CompanyPermissionGroup = {
  name: 'company_individual' | 'company_with_embedded' | 'company_with_embedded_and_orders' | 'company_with_orders';
};

export type TenantCompanyResponse = {
  id: string;
  name: string;
  logo?: string;
  members_count: number;
  owner_email: string;
};

export type KeyDescription = {
  access_key: string;
  env_type: EnvType;
  created_at: string;
  client_id: string;
  webhook_url: string | null;
  last_used_at: string | null;
  user_key_id: string;
};

type KeyGroup = {
  active_keys: KeyDescription[];
  can_create_new_key: boolean;
  title: string;
};

export type UserKeysResponse = Record<EnvType, KeyGroup>;

export type Ordering = 'created_at' | '-created_at' | 'status' | '-status' | 'provider' | '-provider' | '-env' | 'env';

export const allEnvTypes = ['sandbox', 'dev', 'prod'] as const;

export type EnvType = (typeof allEnvTypes)[number];

export type BillingLinkParams = {
  return_url: string;
};

export const PRODUCTS_WITH_EXTERNAL_REPORT = [
  'income',
  'employment',
  'insurance',
  'pll',
  'deposit_switch',
] as VerificationType[];
export type ProductWithExternalReport = (typeof PRODUCTS_WITH_EXTERNAL_REPORT)[number];

export type ReportParams = {
  product_type: ProductWithExternalReport;
  access_token: string;
};

export type RefreshTaskCreateRequest = {
  product_type: VerificationType;
  access_token: string;
  account?: BankAccount;
};

export type RefreshTaskCreateResponse = {
  task_id: string;
};

export type VerificationReport = {
  access_token: string;
  completed_at: string;
  employments?: Employment[];
  id: string;
  pdf_report?: string;
  provider?: SubOrderProvider;
  status: string;
  tracking_info: string | null;
  deposit_details?: BankAccount;
  insurance?: Insurance;
  identity?: Identity;
  data_source: 'payroll' | 'tax' | 'docs';
};

export type PagingFilters = {
  page_size: number;
  page: number;
};

export type BaseFilters = {
  created_at__lte?: string;
  created_at__gte?: string;
  env?: EnvType[];
};

export type UserFilters = BaseFilters &
  PagingFilters & {
    provider?: string;
    query?: string;
    product_type?: VerificationType;
  };

export type TasksOrdering =
  | 'created_at'
  | '-created_at'
  | 'updated_at'
  | '-updated_at'
  | 'status'
  | '-status'
  | 'error_message'
  | '-error_message'
  | 'provider_id'
  | '-provider_id';

export type TasksFilters = {
  query?: string;
  created_at__lte?: string;
  created_at__gte?: string;
  status?: TaskStatus;
  ordering?: TasksOrdering;
  provider?: string;
  product_type?: string;
  user_id?: string;
  cursor?: string;
  page_size?: number;
};

export const TaskStatusMap = {
  DONE: 'done',
  ERROR: 'error',
  LOGIN_ERROR: 'login_error',
  ACCOUNT_LOCKED: 'account_locked',
  MFA_ERROR: 'mfa_error',
  CONFIG_ERROR: 'config_error',
  NO_DATA: 'no_data',
  UNAVAILABLE: 'unavailable',
  UNABLE_TO_RESET: 'unable_to_reset',
  NOT_SUPPORTED: 'not_supported',
} as const;

export type TaskStatus = keyof typeof TaskStatusMap;

export const TaskErrorStatuses = {
  [TaskStatusMap.ERROR]: true,
  [TaskStatusMap.LOGIN_ERROR]: true,
  [TaskStatusMap.MFA_ERROR]: true,
  [TaskStatusMap.CONFIG_ERROR]: true,
  [TaskStatusMap.UNAVAILABLE]: true,
  [TaskStatusMap.NO_DATA]: true,
  [TaskStatusMap.NOT_SUPPORTED]: true,
  [TaskStatusMap.ACCOUNT_LOCKED]: true,
  [TaskStatusMap.UNABLE_TO_RESET]: true,
} as const;

export const TaskTerminalStatuses = {
  ...TaskErrorStatuses,
  [TaskStatusMap.DONE]: true,
} as const;

export type TaskItemProvider = SubOrderProvider;

/**
 * @deprecated Used in internal system API. Use Task instead.
 */
export type TaskItem = {
  bridge_token: string | null;
  product_type: VerificationType;
  data_source: DataSource;
  id: string;
  link_id: string;
  status: ValueOf<typeof TaskStatusMap>;
  environment: EnvType;
  provider: TaskItemProvider | null;
  created_at: string;
  updated_at: string;
  duration: number;
  error_message?: string;
  access_token_id: string;
  tracking_info?: string;
  employer?: string;
  is_data_removed: boolean;
  applicant: Applicant | null;
  is_suspicious: boolean;
};

export type Task = {
  id: string;
  status: ValueOf<typeof TaskStatusMap>;
  product_type: VerificationType;
  provider: TaskItemProvider;
  created_at: string;
  updated_at: string;
  error_message: string | null;
  tracking_info: string | null;
  bridge_token: string | null;
  data_source: DataSource;
  link_id: string;
  is_suspicious: boolean;
  user_id: string;
};

export type RefreshTask = {
  id: string;
  created_at?: string;
  refresh_date: string;
  status: string;
};

type BaseListResult = {
  count: number;
  next: string | null;
  previous: string | null;
};

export type ListResult<T> = BaseListResult & {
  results: T[];
};

export type CursorListResult<T> = {
  next: string | null;
  previous: string | null;
  results: T[];
};

export type LogItem = {
  id: string;
  bridge_token: string | null;
  data: string;
  environment: EnvType;
  errors: string | null;
  headers: string;
  host: string;
  is_webhook: boolean;
  method: string;
  path: string;
  payroll_provider: string | null;
  provider: string | null;
  query_params: string | null;
  request_id: string | null;
  requested_at: string;
  response: string;
  response_headers: string;
  status_code: number;
  link_id: string | null;
  task_id: string | null;
};

export type LogStatusGroup = 'success' | 'client_error' | 'server_error' | 'no_response';

export type LogsFilters = Partial<PagingFilters> & {
  id?: string;
  requested_at__lte?: string;
  requested_at__gte?: string;

  status_code?: string;
  status_group?: LogStatusGroup;
  method?: string;
  path?: string;
  webhook_url?: string;
  event_type?: string;
  is_webhook?: boolean;
  payroll_provider?: string;
  env?: EnvType[];
  search?: string;
};

export type OptionsResult = {
  task_statuses: TaskStatusesMap;
};

export type TaskStatusesMap = {
  [key: string]: string;
};

export type AdditionalProductTypes = 'insurance' | 'transactions';

export type AssetsProductType = 'assets';
export type VerificationType =
  | 'employment'
  | 'income'
  | 'deposit_switch'
  | 'pll'
  | 'admin'
  | AdditionalProductTypes
  | AssetsProductType;

export type ExtendedProductType = VerificationType | 'scoring_attrs';

export type ProductType = ExtendedProductType | AssetsProductType;

export type ReportTabType = VerificationType | 'bank_income';

export enum RemindType {
  OneDay = 'remind_24',
  TwoDays = 'remind_48',
  ThreeDays = 'remind_72',
  OneWeek = 'remind_120',
  TwoWeeks = 'remind_240',
  ThreeWeeks = 'remind_360',
  FourWeeks = 'remind_480',
  TwelveWeeks = 'remind_1440',
}

export enum RemindTypeNew {
  OneDay = '24',
  TwoDays = '48',
  ThreeDays = '72',
  OneWeek = '120',
  TwoWeeks = '240',
  ThreeWeeks = '360',
  FourWeeks = '480',
  TwelveWeeks = '1440',
}

export type FieldGroups = FieldGroup[];

export type FieldGroup = {
  id: string;
  name: string;
  categories: FieldCategory[];
};

export type FieldCategory = {
  id: string;
  name: string;
  mandatory: boolean;
  fields: Field[];
  customizable: boolean;
};

export type Field = {
  id: string;
  name: string;
  description: string;
  mandatory: boolean;
  included: boolean;
};

export type ExcludedFields = {
  [key: string]: {
    [key: string]: string[] | null;
  };
};

export type WidgetSettings = {
  cf_schema: FieldGroups;
  /**
   * Only for PATCH requests, no actual data there
   */
  /* writeonly */ cf_exclude_fields: ExcludedFields;
  client_customer_support_email: string;
  client_name?: string;
  product_type?: VerificationType;
  is_order_invoices_enabled: boolean;
  is_custom_field_enabled: boolean;
  is_tax_product_enabled: boolean;
  custom_field_title?: string | null;
  is_doc_upload_enabled?: boolean;
  is_financial_accounts_enabled?: boolean;
  is_certification_enabled?: boolean | null;
  // Account-level gate for the Freddie AIM Check section on Home Equity templates.
  is_freddie_doc_upload_enabled?: boolean;

  color_primary: string;
  logo?: string;
  client_user_agreement_link?: string;
  client_user_agreement_link_title?: string;
  is_white_background_enabled?: boolean;
  is_get_help_hidden?: boolean;
  is_full_view_enabled?: boolean;
  remind_type: RemindType;

  has_external_search?: boolean;
  custom_domain: string | null;

  orders_product_types: VerificationType[];
  allowed_product_types: ProductType[];
  use_case?: UseCase;
  waterfall_data_sources?: Record<
    VerificationType,
    {
      data_sources: DataSource[];
      flow: DataSourceFlow;
    }
  >;

  disable_transaction_tags: boolean | null;
  disable_use_case_product_names?: boolean | null;
};

export type WebhookSetting = {
  env: EnvType;
  url?: string;
};

export const ALL_SUBORDER_STATUSES = [
  'pending',
  'sent',
  'completed',
  'error',
  'canceled',
  'expired',
  'no_data',
  'skipped',
] as const;

type SuborderStatusTuple = typeof ALL_SUBORDER_STATUSES;

export type SubOrderStatus = SuborderStatusTuple[number];

export const OrderFilterStatuses = {
  'pending': 'Pending',
  'sent': 'Sent',
  'completed': 'Done',
  'expired': 'Expired',
  'canceled': 'Canceled',
  'error': 'Error',
  'skipped': 'Skipped by user',
  'no_data': 'No data',
} as const;

export type OrderFilterStatus = keyof typeof OrderFilterStatuses;

export type Applicant = {
  external_user_id: number | null;
  first_name: string | null;
  id: string;
  last_name: string | null;
  ssn: string | null;
};

export type SubOrderProvider = {
  id: string;
  name: string;
  logo_url?: string;
};

// in Emulator demo results we receive only string, not SubOrderProvider object
export type DemoSubOrder = Omit<SubOrder, 'provider'> & {
  provider: string;
};

/**
 * @todo fix company_name: string | null. Will break in a lot of places
 */
export type SubOrder = {
  id: string;
  access_token: string;
  status: SubOrderStatus;
  order_number: string;
  product_type: VerificationType;
  suborder_number: string;
  start_date: string;
  end_date: string;
  company_name: string;
  created_at: string;
  company_address: CompanyAddress | null;
  company_domain: string;
  company_google_place_id: string;
  company_logo?: string;
  pdf_report?: string;
  employments?: Employment[] | null;
  insurance?: SubOrder;
  completed_at?: string;
  /** @deprecated Use link_deleted_at and link_credentials_deleted_at instead */
  is_data_removed: boolean;
  is_suspicious?: boolean;
  last_payment: Payment | null;
  last_task_id?: string;
  link_id?: string;
  deposit_details?: BankAccount;
  provider?: SubOrderProvider;
  task_refresh_count?: number;
  data_source: DataSource;
  link_deleted_at?: string | null;
  link_credentials_deleted_at?: string | null;
};

export type AssetsLink = {
  link_id: string;
  tracking_info?: string;
  provider: string;
  provider_info?: TaskItemProvider;
  identities: Identity[];
  accounts: AssetsAccount[];
  transactions: Transaction[];
  owners: {
    id: string;
    full_name: string;
    email: string;
    phone: string;
    address: Address;
  }[];
};

export type Borrower = {
  id: string;
  external_user_id: string;
  first_name: string;
  last_name: string;
  email: string;
  phone: string;
  ssn: string;
  created_at: string;
  updated_at: string;
};

type AssetsSummary = {
  avg_30: string | null;
  avg_60: string | null;
  avg_90: string | null;
  currency_code: string;
  balance: string | null;
};

export type AssetsOrder = {
  report_id: string;
  created_at: string;
  completed_at: string;
  days_requested: number;
  large_deposit_threshold?: number | null;
  borrower: Borrower;
  links: AssetsLink[];
  summary: AssetsSummary;
  is_voe: boolean;
};

export type AssetsRequest = {
  days_requested?: number;
  large_deposit_threshold?: LargeDepositThreshold;
  is_voe?: boolean;
  account_ids?: string[];
};

export type LargeDepositThreshold = {
  fixed_amount?: number;
  sales_price?: number;
  qualifying_monthly_income?: number;
  loan_type?: 'FHA' | 'CONVENTIONAL' | 'OTHER';
};

export type CompanyAddress = {
  city?: string;
  state?: string;
  street?: string;
  zip?: string;
};

export type DataSource =
  | 'payroll'
  | 'docs'
  | 'insurance'
  /* @deprecated */
  | 'transactions'
  | 'financial_accounts'
  | 'scoring_attributes'
  | 'assets'
  | 'tax';

export enum SourceType {
  FLOIFY = 'floify',
  BESMARTEE = 'besmartee',
  LENDERLOGIX = 'lenderlogix',
  ENCOMPASS_CONSUMER_CONNECT = 'encompass_consumer_connect',
  BYTE = 'byte',
  CORE_LOGIC = 'core_logic',
  XACTUS = 'xactus',
  CONSTELLATION = 'constellation',
  BANNO = 'banno',
  MX = 'mx',
  Q2 = 'q2',
  CLUTCH = 'clutch',
  ACCIO = 'accio',
  ENCOMPASS = 'encompass',
  TPO_CONNECT = 'tpo_connect',
  DARKMATTER = 'darkmatter',
  TAZWORKS = 'tazworks',
  INTERNAL = 'internal',
  SIMPLENEXUS = 'simplenexus',
  EXTERNAL_WEBPAGE = 'external_webpage',
  INDIVIDUAL = 'individual',
  SELF_SIGNUP = 'self_signup',
  ALKAMI = 'alkami',
  BLUE_SAGE = 'blue_sage',
  LODASOFT = 'lodasoft',
  BLEND = 'blend',
}

export type HiddenFromTimelineOrderEventType = 'refresh_failed';

export type OrderGroupEventType =
  | 'order_created'
  | 'order_completed'
  | 'suborder_canceled'
  | 'suborder_expired'
  | 'suborder_completed'
  | 'suborder_skipped'
  | 'suborder_no_data'
  | 'email_first_sent'
  | 'email_first_delivery_error'
  | 'email_first_unsubscribed'
  | 'email_first_link_opened'
  | 'email_first_delivered'
  | 'sms_first_sent'
  | 'sms_first_unsubscribed'
  | 'sms_first_link_opened'
  | 'sms_first_delivered'
  | 'sms_first_delivery_error'
  | 'last_refresh'
  | 'login_attempt'
  | HiddenFromTimelineOrderEventType;

export type TimelineVisibleEventType = Exclude<OrderGroupEventType, HiddenFromTimelineOrderEventType>;

export type OrderGroupEvent = {
  title: OrderGroupEventType;
  timestamp?: string;
  suborder_id: string | null;
  is_refresh_order_event: boolean;
};

export type OrderEventsResponse = {
  results: OrderEvent[];
};

export type OrderEvent = {
  /** The type of object this event relates to */
  object: 'order' | 'notification' | 'link';
  /** The specific action or state change that occurred */
  type: OrderEventType;
  /** Whether this event belongs to a refresh order */
  is_refresh: boolean;
  /** ID of the related suborder (employer, financial institution, etc.) */
  suborder_id?: string | null;
  /** ID of the related task */
  task_id?: string | null;
  /** ID of the related link */
  link_id?: string | null;
  /** ID of the related order that produced this event (initial or refresh) */
  order_id: string;
  /** Timestamp when the event occurred (ISO 8601) */
  timestamp: string;
};

export type OrderEventType =
  | 'order_created'
  | 'login_attempt'
  | 'suborder_completed'
  | 'suborder_canceled'
  | 'suborder_expired'
  | 'suborder_no_data'
  | 'suborder_skipped'
  | 'email_first_sent'
  | 'email_first_delivery_error'
  | 'email_first_delivered'
  | 'email_first_link_opened'
  | 'email_first_unsubscribed'
  | 'email_reminder_sent'
  | 'email_reminder_delivery_error'
  | 'email_reminder_delivered'
  | 'email_reminder_link_opened'
  | 'email_reminder_unsubscribed'
  | 'sms_first_sent'
  | 'sms_first_delivery_error'
  | 'sms_first_delivered'
  | 'sms_first_link_opened'
  | 'sms_first_unsubscribed'
  | 'sms_reminder_sent'
  | 'sms_reminder_delivery_error'
  | 'sms_reminder_delivered'
  | 'sms_reminder_link_opened'
  | 'sms_reminder_unsubscribed'
  | 'certification_completed';

export type SelfCertificationResultsResponse = {
  results: SelfCertificationResult[];
};

export type SelfCertificationResult = {
  // `pending` = certification requested but not yet completed; only the two terminal decisions render a chip.
  decision: 'representative' | 'not_representative' | 'pending';
  rejection_reason?:
    | 'employment_no_longer_active'
    | 'incorrect_amount'
    | 'duplicate_entry'
    | 'outdated_information'
    | 'other'
    | null;
  rejection_comment?: string | null;
  employment?: SelfCertificationEmployment;
  account?: IncomeCertificationAccount;
};

export const JOB_TYPE = {
  F: 'Full Time',
  P: 'Part Time',
  S: 'Seasonal',
  D: 'Daily (per diem)',
  C: 'Contract',
  V: 'Volunteer',
};

export type JobType = keyof typeof JOB_TYPE;

export type SelfCertificationEmployment = {
  id: string;
  job_type?: JobType;
  start_date?: string | null;
  end_date?: string | null;
  pay_rate?: string | null;
  pay_date?: string | null;
  pay_frequency?: PayFrequencyType | null;
  gig?: {
    monthly_income?: string;
    hours_per_week?: number;
  } | null;
  company: {
    name: string;
  };
};

export type IncomeCertificationAccount = {
  id: string;
  mask: string;
  routing_number: string;
  owners: {
    full_name: string;
  }[];
  balance?: string | null;
  type:
    | 'CHECKING'
    | 'SAVINGS'
    | 'LOAN'
    | 'CREDIT_CARD'
    | 'INVESTMENT'
    | 'LINE_OF_CREDIT'
    | 'MORTGAGE'
    | 'PROPERTY'
    | 'CASH'
    | 'INSURANCE'
    | 'PREPAID'
    | 'CHECKING_LINE_OF_CREDIT';
};

export type StrictOrderProductType =
  | [VerificationType]
  | [Exclude<VerificationType, AdditionalProductTypes>, AdditionalProductTypes]
  | [AdditionalProductTypes, Exclude<VerificationType, AdditionalProductTypes>];

export type PaymentStatus = 'requires_payment_method' | 'requires_action' | 'processing' | 'succeeded' | 'canceled';

export type Payment = {
  status: PaymentStatus;
  /** Why this payment failed, already mapped to customer-facing copy. Null when nothing failed. */
  failure_message?: string | null;
};

export type FinancialSubAccount = {
  id: string;
  mask: string;
  account_type: string;
};

export type FinancialAccount = SubOrder & {
  // child bank accounts, e.g. Checking *1234
  accounts: FinancialSubAccount[];
  // only present when the order group is fetched with the `certification` param
  additional_income_sources?: UserReportedIncomeSource[] | null;
  // detected bank income from income data; present only for bank_income self-cert orders
  bank_income_sources?: BankIncomeInsightsSource[] | null;
  bank_income_summary?: BankIncomeSummary | null;
};

// Per-suborder shape returned by the `/v1/orders/` list endpoint inside
// `employers` / `insurance` / `financial_accounts`. Strict subset of `SubOrder`.
export type SubOrderListItem = {
  id: string;
  product_type: VerificationType;
  status: SubOrderStatus;
  suborder_number: string | null;
  created_at: string;
  link_id: string | null;
  data_source: DataSource | null;
  provider: SubOrderProvider | null;
  is_suspicious: boolean;
  was_attempted: boolean;
};

// Detail shape returned by `/v1/order_groups/{id}/`. Kept rich because
// downstream code (action menu, detail page, cancel/edit flows) reads many
// fields the list endpoint deliberately omits.
export type OrderGroup = {
  applicant: Applicant | null;
  canceled_at: string | null;
  cc_emails?: string[];
  certification_mode?: SelfCertificationMode | null;
  company_logo?: string;
  company_name?: string;
  created_at: string;
  custom_field?: string;
  email?: string;
  env?: EnvType;
  events: OrderGroupEvent[];
  expired_at: string;
  extra_data?: {
    refreshed_from_order?: string;
  };
  financial_accounts: FinancialAccount[] | null;
  /**
   * @deprecated soon
   */
  first_name: string;
  has_consent_form: boolean | null;
  has_paid_source: boolean;
  id: string;
  income_insights_report_id: string | null;
  initial_order?: string;
  insurance: SubOrder;
  invoice: null | {
    status: 'pending' | 'success' | 'error';
    file: string;
  };
  /**
   * @deprecated soon
   */
  last_name: string;
  last_payment: Payment | null;
  last_reminded_at: string;
  loan: {
    clear_to_close_date: string | null;
    closing_date: string | null;
    external_id: string | null;
    loan_number: string | null;
    originator_name: string | null;
    originator_email: string | null;
    loan_processor_email: string | null;
    loan_processor_name: string | null;
  } | null;
  /**
   * @deprecated
   */
  loan_number: string | null;
  manager: null | {
    name: string;
    email: string;
  };
  notes?: string | null;
  order_number?: string;
  phone?: string;
  products: StrictOrderProductType;
  refresh_order: string | null;
  refresh_order_waits_for_approval?: boolean;
  refresh_suborders: SubOrder[];
  refreshed_at: string | null;
  share_url: string | null;
  short_share_url: string | null;
  source: SourceType;
  suborders: SubOrder[];
  template?: {
    id: string;
    name: string;
  };
  user_id: string;
  voa_report_id: string | null;
  voie_report_id: string | null;
};

// Lean row shape returned by the public `/v1/orders/` list endpoint.
// Drops the heavy fields detail callers read (events, applicant, invoice,
// report ids, payments, etc.). Suborders are split into product-type buckets.
export type OrderListItem = {
  id: string;
  user_id: string;
  // Only present when the list is fetched with `distinct_by_user`; signals the
  // applicant has more orders than the single most-recent one returned here.
  has_additional_orders?: boolean;
  order_number: string | null;
  products: StrictOrderProductType;
  source: SourceType;
  custom_field: string | null;
  first_name: string;
  last_name: string;
  short_share_url: string | null;
  created_at: string;
  updated_at: string;
  expired_at: string | null;
  canceled_at: string | null;
  completed_at: string | null;
  initial_order: string | null;
  refresh_order: string | null;
  manager: null | {
    name: string;
    email: string;
  };
  loan: null | {
    loan_number: string | null;
    originator_name: string | null;
    originator_email: string | null;
    loan_processor_name: string | null;
    loan_processor_email: string | null;
  };
  template: null | {
    id: string;
    name: string;
  };
  notes: string | null;
  employers: SubOrderListItem[];
  insurance: SubOrderListItem | null;
  financial_accounts: SubOrderListItem[] | null;
};

export type OrderGroupsResponse = CursorListResult<OrderListItem>;

// Server supports `refreshed_at` ordering (annotated on the QuerySet) even
// though it isn't part of the list response shape, so keep it sortable.
export const ORDERS_SORTABLE_FIELDS = ['created_at', 'refreshed_at', 'notes', 'order_number', 'manager.email'] as const;
export const ORDER_SORTABLE_FIELDS_SET = new Set(ORDERS_SORTABLE_FIELDS);

export type OrdersSortableField = (typeof ORDERS_SORTABLE_FIELDS)[number];
export type OrdersOrdering = OrdersSortableField | `-${OrdersSortableField}`;

export type OrderManager = {
  id: string;
  email: string;
  name: string | null;
};

export type OrderRefreshFilter = 'only' | 'exclude';

export type OrderGroupsParams = Partial<PagingFilters> &
  Partial<{
    status: OrderFilterStatus[];
    created_at__lte: string;
    created_at__gte: string;
    expired_at__lte: string;
    expired_at__gte: string;
    source: string;
    products: VerificationType[];
    search: string;
    ordering: OrdersOrdering;
    env?: EnvType[];
    order_manager_ids?: string;
    refresh?: OrderRefreshFilter;
    cursor?: string;
    // Collapse the list to the most-recent order per applicant.
    distinct_by_user: boolean;
    // Filter to a single applicant's orders (used to load an expanded group).
    user_id: string;
  }>;

export type CreateOrderGroup = Partial<OrderGroup> & {
  products: StrictOrderProductType;
  source: string;
  first_name: string;
  last_name: string;
  ssn?: string;
  custom_field?: string;
  template_id?: string;
  cc_emails?: string[];
};

export type CreateOrderGroupParams = {
  order_group: CreateOrderGroup;
  employments?: CreateOrderGroupEmployment[];
  financial_institutions?: {
    id?: string;
    name: string;
    suborder_number?: string;
  }[];
  insurance?: {
    provider_id: string;
  };
  reports?: {
    voa?: {
      days_requested?: number;
      request_extended_history?: boolean;
      large_deposit_threshold?: LargeDepositThreshold;
    };
    income_insights?: {
      days_requested?: number;
      consumer_report_permissible_purpose?: ConsumerReportPermissiblePurpose;
    };
  };
  env: EnvType;
};

export type CreateOrderGroupEmployment = {
  company_address?: string;
  company_domain?: string;
  company_google_place_id?: string;
  company_logo?: string;
  company_name?: string;
  end_date?: string;
  start_date?: string;
  account?: BankAccount;
};

export const REFRESHABLE_PRODUCT_TYPES = ['income', 'employment', 'insurance', 'assets', 'transactions'] as const;

export type RefreshableProductType = (typeof REFRESHABLE_PRODUCT_TYPES)[number];

export type RefreshOrderEmployer = {
  id: string;
};

export type RefreshOrderRequest = {
  products?: RefreshableProductType[];
  employers?: RefreshOrderEmployer[];
  financial_accounts?: RefreshOrderEmployer[];
  include_recent_paystub?: boolean;
  env: EnvType;
  reports?: {
    voa?: {
      account_ids?: string[];
      days_requested?: number;
      is_voe?: boolean;
    };
  };
};

export type RefreshOrderResponse = OrderGroup;

export type EditOrderRequest = {
  env: EnvType;
  first_name?: string;
  last_name?: string;
  refresh_approved?: boolean;
  ssn?: string;
  notes?: string | null;
};

export type OrderFileResponse = {
  id: string;
  order_group_id: string;
  file: string;
  created_at: string;
  updated_at: string;
  status: 'pending' | 'success' | 'error';
};

export type Address = {
  street?: string;
  city?: string;
  state?: string;
  country?: string;
  zip?: string;
};
export type Company = {
  name?: string;
  address?: Address;
  phone?: string;
};

export type BankAccount = {
  account_name?: string | null;
  account_number?: string | null;
  account_type?: string | null;
  bank_name?: string | null;
  bank_address?: string | null;
  deposit_type?: DepositType | null;
  deposit_value?: string | number | null;
  routing_number?: string | null;
};

export type DepositType = 'entire' | 'percent' | 'amount';

export type AnnualSummary = {
  id: string | null;
  year: number | null;
  regular: string | null;
  bonus: string | null;
  commission: string | null;
  overtime: string | null;
  other_pay: string | null;
  net_pay: string | null;
  gross_pay: string | null;
};

export type W2 = {
  federal_tax: string | null;
  file: string | null;
  gross_pay: string | null;
  md5sum: string | null;
  medicare_tax: string | null;
  medicare_wages: string | null;
  social_security_tax: string | null;
  social_security_wages: string | null;
  wages: string | null;
  year: number | null;
};

export type NotEmptyW2 = W2 & { file: string; year: number };

export type Employment = {
  annual_salary: string;
  id: string;
  job_title: string;
  job_type: JobType;
  start_date: string | null;
  end_date: string | null;
  employed_in_role: string | null;
  external_last_updated: string;
  hourly_salary: string;
  original_hire_date?: string;
  company?: Company;
  profile: Identity;
  statements: Statement[];
  total_statements_count: number | null;
  comment: string;
  do_not_contact: string;
  dates_from_statements?: boolean;
  is_active?: boolean;
  manager_name?: string;

  income?: string | null;
  income_unit?: string | null;
  pay_frequency?: PayFrequencyType | null;
  annual_income_summary?: AnnualSummary[];
  bank_accounts?: BankAccount[];
  w2s?: W2[];
  certification: Pick<SelfCertificationResult, 'decision' | 'rejection_reason' | 'rejection_comment'> | null;
};

type Deduction = {
  name: string;
  amount: string;
  category: string;
};

type Earning = {
  name: string;
  amount: string;
  category: string;
  rate: string | null;
  units: string | null;
};

export type Statement = {
  id: string;
  pay_date: string;

  net_pay: string | null;
  net_pay_ytd: string | null;

  deductions: Deduction[] | null;
  deductions_ytd: Deduction[] | null;

  earnings: Earning[] | null;
  earnings_ytd: Earning[] | null;

  gross_pay: string | null;
  gross_pay_ytd: string | null;

  hours: string | null;
  basis_of_pay: string | null;

  period_start: string | null;
  period_end: string | null;

  regular: string | null;
  regular_ytd: string | null;

  bonus: string | null;
  bonus_ytd: string | null;

  commission: string | null;
  commission_ytd: string | null;
  overtime: string | null;
  overtime_ytd: string | null;
  other_pay: string | null;
  other_pay_ytd: string | null;

  md5sum: string | null;
  file: string | null;
  check_number: string | null;
  derived_fields: string[];
  missing_data_fields: string[];
};

export type Invite = {
  id: string;
  status: 'pending' | 'expired' | 'completed' | 'error';
  created_at: string;
  expired_at: string;
};

export type RoleId = 'administrator' | 'developer' | 'orders_manager' | 'orders_tasks_manager' | 'owner';

export type Role = {
  id: RoleId;
  name: string;
  description: string;
};

export type GetMembersParams = Partial<PagingFilters> & {
  search?: string;
  is_approved?: boolean | 'null';
  ordering?: string;
  invite_status?: InviteStatus;
  access_to_templates?: string;
  role?: string;
};

export enum InviteStatus {
  INVITED = 'invited',
  WAITING_FOR_APPROVAL = 'waiting_for_approval',
}

export const INCOME_CATEGORY_LABEL_MAP = {
  PAYCHECK: 'Payroll',
  GOVERNMENT_EMPLOYMENT: 'Payroll',
  GIG_ECONOMY: 'Gig',
  RETIREMENT: 'Benefits',
  UNEMPLOYMENT: 'Benefits',
  GOVERNMENT_BENEFITS: 'Benefits',
  PRIVATE_BENEFITS: 'Benefits',
  RENTAL: 'Rental',
  EWA_PAYROLL: 'EWA',
  P2P_TRANSFER: 'P2P',
  TAX_CREDITS: 'Tax',
  CASH_OR_CHECK: 'Cash',
} as const;

export type IncomeCategory = keyof typeof INCOME_CATEGORY_LABEL_MAP;

export type Member = {
  user_id: string;
  first_name: string;
  last_name: string;
  email: string;
  created_at: string;
  is_active: boolean; // use it later to temporary disable users
  is_approved: boolean | null;
  invite?: Invite;
  role: Role;
  access_to_templates: string[];
};

export type UpdateMemberResult = {
  user_id: string;
  first_name: string;
  last_name: string;
  role: RoleId;
  access_to_templates: string[];
};

export type CreateMemberResult = {
  user_id: string;
  email: string;
  first_name: string;
  last_name: string;
  role: RoleId;
  access_to_templates: string[];
};

export type ProviderBase = {
  id: string;
  name: string;
  logo_url?: string;
  success_rate?: SuggestionSuccessRate;
};

export type Provider = ProviderBase & {
  data_source: string;
  is_disabled: boolean;
};

export type WidgetProvidersRequest = {
  bridgeToken: string;
  productType?: VerificationType;
  dataSource?: DataSource[];
  listId: string | null;
};

export type ProvidersListResponse = ListResult<Provider>;

export type ProvidersListRequest = Partial<PagingFilters> & {
  data_source?: DataSource;
  product_type: VerificationType;
  query?: string;
  ordering?: 'id' | '-id' | 'name' | '-name' | 'rank' | '-rank';
};

export type CategorizedCompanyMappingBase = {
  name: string;
  logo_url: string;
  success_rate?: SuggestionSuccessRate;
  confidence_level: string;
  sso: {
    name: string;
  };
};

export type CategorizedCompanyMappingWidget = CategorizedCompanyMappingBase & {
  domain: string;
  company_mapping_id: string;
};

export type CategorizedCompaniesTypeBase = {
  id: CompaniesCategoryId;
  name: string;
};

export type CategorizedCompaniesType = CategorizedCompaniesTypeBase & {
  mappings: CategorizedCompanyMappingBase[];
};

export type CategorizedCompaniesWidget = CategorizedCompaniesTypeBase & {
  mappings: CategorizedCompanyMappingWidget[];
  show_search_item?: boolean | null;
  is_visible?: boolean;
};

export type InsurancePolicy = {
  carrier_name: string;
  canceled_date: string | null;
  carrier_policy_number: string;
  claims: InsuranceClaim[];
  commercial_named_insureds: InsuranceCommercialNamedInsured[];
  created_at: string;
  deductible_cents: null;
  description: string;
  documents: InsuranceDocument[];
  dwellings: InsuranceDwelling[];
  effective_date: string;
  expiry_date: string;
  id: string;
  loss_events: InsuranceLossEvent[];
  name: string;
  policy_type: string;
  renewal_date: string | null;
  status: string;
  total_premium_cents: number;
  updated_at: string;
  vehicles: InsuranceVehicle[];
};

export type InsuranceCommercialNamedInsured = {
  id: string;
  name: string;
  form_of_business: string;
  gl_code: string;
  sic_code: string;
  naics_code: string;
  fein: string;
  ssn: string;
  is_primary: boolean;
  address: Address;
};

export type InsuranceLossEvent = {
  id: string;
  date_of_occurrence: string;
  date_of_claim: string;
  type: string;
  amount_paid_cents: number;
  amount_reserved_cents: number;
  is_subrogation: boolean;
  is_claim_open: boolean;
};

export type InsuranceClaim = {
  id: string;
  dwelling_id: string;
  vehicle_id: string;
  driver_id: string;
  carrier_claim_identifier: string;
  date_occurred: string;
  type: string;
  status: string;
  date_closed: string;
  payout_cents: number;
  representative_name: string;
  representative_phone: string;
  representative_email: string;
  address: Address;
};

export type InsuranceDocument = {
  id: string;
  title: string;
  document_type: string;
  date_added: string;
  file: string;
};

export type InsuranceCoverage = {
  name: string;
  premium_cents: number;
  per_person_limit_cents: number;
  per_incident_limit_cents: number;
  deductible_cents: number;
  is_declined: boolean;
  id: string;
  per_day_limit_cents: number;
  per_mile_premium_tenth_of_cents: number;
};

export type InsuranceMortgagee = {
  id: string;
  type: string;
  loan_number: string;
  name: string;
  address: Address;
};

export type InsuranceVehicle = {
  id: string;
  year: number;
  make: string;
  model: string;
  series: string;
  series2: string;
  type: string;
  annual_mileage: number | null;
  vin: string;
  purchase_date: string | null;
  is_removed: boolean;
  lien_holder: string;
  garaging_address: Address;
  lien_holder_address: Address;
  coverages: InsuranceCoverage[];
  drivers: InsuranceDriver[];
};

export type InsuranceDwelling = {
  id: string;
  address: Address;
  coverages: InsuranceCoverage[];
  mortgagees?: InsuranceMortgagee[];
};

export type InsuranceDriver = {
  first_name: string;
  id: string;
  middle_name: string;
  last_name: string;
  drivers_license: string;
  drivers_license_state: string;
  date_of_birth_str: string;
  gender: string;
  marital_status: string;
  relationship_to_insured: string;
  is_excluded: true;
};

export type InsuranceDrivingRecords = {
  id: string;
  driver_id: string;
  incident_date: string;
  incident_type: string;
  violation_type: string;
  is_at_fault: boolean;
};

export type Insurance = {
  policies: InsurancePolicy[];
  drivers: InsuranceDriver[];
  driving_records: InsuranceDrivingRecords[];
};

export type AccountOwner = {
  address: Address;
  email: string;
  full_name: string;
  id: string;
  phone: string | null;
  relation_type: RelationType;
};

export type RelationType =
  | 'AUTHORIZED_USER'
  | 'BUSINESS'
  | 'FOR_BENEFIT_OF'
  | 'FOR_BENEFIT_OF_PRIMARY'
  | 'FOR_BENEFIT_OF_PRIMARY_JOINT_RESTRICTED'
  | 'FOR_BENEFIT_OF_SECONDARY'
  | 'FOR_BENEFIT_OF_SECONDARY_JOINT_RESTRICTED'
  | 'FOR_BENEFIT_OF_SOLE_OWNER_RESTRICTED'
  | 'POWER_OF_ATTORNEY'
  | 'PRIMARY_JOINT_TENANTS'
  | 'PRIMARY'
  | 'PRIMARY_BORROWER'
  | 'PRIMARY_JOINT'
  | 'SECONDARY'
  | 'SECONDARY_JOINT_TENANTS'
  | 'SECONDARY_BORROWER'
  | 'SECONDARY_JOINT'
  | 'SOLE_OWNER'
  | 'TRUSTEE'
  | 'UNIFORM_TRANSFER_TO_MINOR';

export type AccountBase = {
  id: string;
  created_at: string;
  updated_at: string;
  type: string | null;
  subtype: string | null;
  mask: string | null;
  nickname: string | null;
  owners: AccountOwner[];
};

export type AssetsAccount = AccountBase & {
  balances: {
    available_balance: string | null;
    balance: string;
    credit_limit: string | null;
    currency_code: string | null;
  };
  direct_deposit_from_employer: boolean;
  same_owner_as_requested: boolean;
  summary: AssetsSummary;
  nsf: number;
  days_available: number;
  transactions: Transaction[];
  routing_number: string;
};

export type TransactionsAccount = {
  account_number: string;
  account_type: string;
  balances: {
    currency_code: string | null;
    balance: string;
  };
  external_id: string;
  id: string;
  is_closed: boolean;
  nickname: string | null;
  owners: AccountOwner[] | null;
  provider: string;
  routing_number: string;
  mask: string;
  type: string;
};
export type TransactionStatus = 'POSTED' | 'PENDING';
export type TransactionType = 'DEBIT' | 'CREDIT';
export type Transaction = {
  account_id: string;
  amount: string;
  categories: string[];
  check_number: string | null;
  currency_code: string | null;
  description: string;
  ending_daily_balance: string | null;
  external_id: string;
  id: string;
  merchant_category_code: string | null;
  posted_at: string;
  status: TransactionStatus;
  transacted_at: string;
  type: TransactionType;
  is_subscription: boolean;
  is_direct_deposit: boolean;
};

export type TransactionsResponse = {
  next: string | null;
  previous: string | null;
  count: number;
  results?: Transaction[];
  transactions: Transaction[];
  accounts: TransactionsAccount[];
};

export type Identity = {
  created_at: string;
  date_of_birth: string;
  email: string;
  first_name: string;
  full_name: string;
  home_address: Address;
  id: string;
  last_name: string;
  middle_initials: string;
  phone?: string | null;
  ssn: string | null;
  updated_at: string;
};

export type VerificationResult = {
  large_deposit_threshold?: number;
  status?: 'full_parse' | keyof typeof TaskTerminalStatuses;
  data_source?: DataSource;
  payroll_report_id?: string;
  deposit_details?: BankAccount;
  insurance?: Insurance;
  identity?: Identity;
  pdf_report?: string;
  transactions?: TransactionsResponse;
  tax_documents?: W2[];
  // it's needed only for transactions, because of broken pagination TODO - transactions pagination
  link_id?: string;
};

export type VerificationRequestBody = {
  access_token: string;
  start_date?: string;
  end_date?: string;
};

export type ImpersonateResponse = {
  id: string;
  target: string;
};

// combinations are alphabet sorted! income always comes first, then insurance, then transactions
export type TemplatesProductCombinations =
  | VerificationType
  | 'income,insurance'
  | 'assets,income'
  | 'assets,insurance'
  | 'assets,income,insurance'
  | '';

export type TemplateBridgeTexts = {
  /** @deprecated use product_settings instead, remove after migration */
  search_header?: string;
  success_cta?: string;
  success_title?: string;
  success_subtitle?: string;
};

export type CompaniesCategoryId =
  | 'popular'
  | 'popular_sandbox'
  | 'employers'
  | 'providers'
  | 'gig'
  | 'unemployment'
  | 'other'
  | 'unemployment_mapped'
  | 'tax'
  | 'peo';

export type TemplateBridgeSearch = {
  popular_listing_id?: string | null;
  categories?: CompaniesCategoryId[];
};

export type TemplateProductSetting = {
  product_type: VerificationType;
  data_source?: DataSource;
  popular_provider_listing_id?: string | null;
  texts?: {
    search_header?: string;
  };
};

export type TemplateBridge = {
  texts?: TemplateBridgeTexts;
  search?: TemplateBridgeSearch;
  product_settings?: TemplateProductSetting[];
};

export type TemplateBranding = {
  company_name?: string | null;
  logo?: string | File;
  accent_color?: string;
  background_color?: string;
  hide_confetti?: boolean;
  custom_end_user_agreement?: {
    header: string | null;
    url: string | null;
  };
  // The backend only sends a name for the products a bridge token allows, so every key is optional.
  // null is how a cleared name is sent back: it resets the product to the backend default.
  custom_product_names?: {
    pll?: string | null;
    income?: string | null;
    assets?: string | null;
    employment?: string | null;
    insurance?: string | null;
  };
};

export type TemplateEmailSettings = {
  subject?: string;
  title?: string;
  header?: string;
  button?: string;
  caption?: string;
};

export type TemplateFirstEmailSettings = TemplateEmailSettings & {
  apply_for_reminder?: boolean;
};

export type TemplateSMSSettings = {
  text?: string;
};

export type TemplateFirstSMSSettings = TemplateSMSSettings & {
  apply_for_reminder: boolean;
};

export type TemplateLandingSettings = {
  header?: string;
  body?: string;
};

export type BankAccountType = 'checking' | 'savings';

export type TemplateSelfCertificationCommunityEngagement = {
  enabled: boolean;
  text?: string | null;
};

export type TemplateSelfCertificationNonCertifiableIncome = {
  enabled: boolean;
  header?: string | null;
  text?: string | null;
};

export const SELF_CERTIFICATION_MODES = ['financial_accounts', 'bank_income'] as const;
export type SelfCertificationMode = (typeof SELF_CERTIFICATION_MODES)[number];

export type TemplateSelfCertificationSettings = {
  main_header?: string | null;
  main_body?: string | null;
  deposits_header?: string | null;
  deposits_body?: string | null;
  community_engagement?: TemplateSelfCertificationCommunityEngagement;
  non_certifiable_income?: TemplateSelfCertificationNonCertifiableIncome;
  caption?: string | null;
  button?: string | null;
  certification_mode?: SelfCertificationMode | null;
};

export type TemplateOrder = {
  custom_field_title?: string | null;
  first_email?: TemplateFirstEmailSettings;
  first_sms?: TemplateFirstSMSSettings;
  landing?: TemplateLandingSettings & {
    hide_faq: boolean;
    success_screen?: TemplateLandingSettings;
    expired_screen?: TemplateLandingSettings;
  };
  link_expiration?: RemindTypeNew;
  reminder_email?: TemplateEmailSettings;
  reminder_sms?: TemplateSMSSettings;
  support_email?: string | null;
  bank_info?: {
    bank_name?: string;
    bank_address?: string;
    account_number?: string;
    routing_number?: string;
    account_type?: BankAccountType;
  };
  self_certification?: TemplateSelfCertificationSettings;
  notification_settings?: {
    suppress_user_notifications?: boolean | null;
    first_notification_delay_hours?: number | null;
  };
  employment_duration_requirement?: EmploymentDurationRequirement;
  auto_open?: AutoOpenValue | null;
  allow_additional_connections?: VerificationType[] | null;
  // Hours to wait after the most recent successful connection before auto-closing a partial order.
  // 1–720 (30 days); null inherits the backend default of 24h.
  auto_complete_after_hours?: number | null;
  // Per-product skip behavior. Keys must be in the template's products list. Only "expire" also
  // auto-closes the order after `auto_complete_after_hours`; the others just gate the skip action.
  skip_behavior?: OrderSkipBehavior | null;
};

export const ORDER_SKIP_BEHAVIOR_EXPIRE = 'expire' as const;
// disabled: product required · all: skip after every provider tried · any: skip after one provider
// tried · expire: same as `any`, plus the order auto-completes after `auto_complete_after_hours`.
export type OrderSkipBehaviorValue = 'disabled' | 'all' | 'any' | typeof ORDER_SKIP_BEHAVIOR_EXPIRE;
export type OrderSkipBehavior = Partial<Record<VerificationType, OrderSkipBehaviorValue>>;

export const AUTO_OPEN_DISABLED = 'disabled' as const;
export type AutoOpenProduct = Extract<VerificationType, 'income' | 'assets' | 'insurance'>;
export type AutoOpenValue = AutoOpenProduct | typeof AUTO_OPEN_DISABLED;

export type EmploymentDurationUnit = 'years' | 'months' | 'days';

export type EmploymentDurationRequirement = {
  enabled: boolean;
  minimum_duration: {
    value: number | null;
    unit: EmploymentDurationUnit;
  };
  warning_header?: string | null;
  warning_body?: string | null;
};

export type DocUploadConfig = {
  title: string;
  submit_title: string;
  description: string;
  min_count: number;
  max_count: number;
  max_bytes: number;
  allowed_mimetypes: string[];
  is_required: boolean;
  is_enabled: boolean;
  name: string;
  approve_page_header: string;
  allowed_products: VerificationType[];
  button: string;
};

export const DOCUMENT_TYPES = ['paystub', 'w2', 'f1099', 'f1040', 'volunteer_letter', 'other'] as const;
export type DocumentType = (typeof DOCUMENT_TYPES)[number];

export type TemplateFreddieAimCheck = {
  is_enabled?: boolean | null;
  seller_id?: string | null;
};

export type TemplateDocUpload = Partial<Record<DocumentType, DocUploadConfig>> & {
  is_enabled: boolean;
  title?: string | null;
  button?: string | null;
  freddie_aim_check?: TemplateFreddieAimCheck;
  _order?: { type: DocumentType }[];
};

export type TemplateReportSection = {
  deposits?: boolean;
  historical_payment_summary?: boolean;
  large_deposits?: boolean;
};

export type TemplateSettings = {
  name: string;
  products: VerificationType[];
  reports: {
    hidden_sections: TemplateReportSection;
    days_requested: number | null;
    large_deposit_threshold: number | null;
    transaction_categories?: string[] | null;
    transaction_account_types?: string[] | null;
    transaction_account_subtypes?: string[] | null;
    labeled_transaction_categories?: string[] | null;
  };
  data: {
    paystubs_ytd_count?: number;
    paystubs_count?: number;
    w2_count?: number;
    bank_statements_count?: number | null;
    is_enabled: boolean;
    extended_history?: {
      statements_history_days: number | null;
    };
  };
  default_for_products?: boolean;
  bridge?: TemplateBridge;
  branding?: TemplateBranding;
  orders?: TemplateOrder;
  document_upload?: TemplateDocUpload;
  manager?: {
    name: string;
    email: string;
  };
  data_sources_flow?: Partial<
    Record<
      VerificationType,
      {
        data_sources?: DataSource[];
        flow?: DataSourceFlow;
      }
    >
  >;
};

export type GetTemplateResponse = TemplateSettings & {
  id: string;
  created_at: string;
  updated_at: string | null;
};

export type GetTemplatesResponse = ListResult<GetTemplateResponse>;

export type UpdateTemplateProps = {
  id: string;
  payload: Partial<TemplateSettings>;
};

export type GetTemplatesRequest = PagingFilters & {
  search?: string;
  products?: TemplatesProductCombinations;
};

export type GetPopularCompaniesListIdResponse = {
  id: string;
};

export type GetPopularProvidersListIdResponse = {
  id: string;
};

export type CreateBridgeUserParams = {
  external_user_id: string;
  first_name?: string;
  last_name?: string;
  email?: string;
  phone?: string;
  ssn?: string;
};

export type BridgeUserResponse = CreateBridgeUserParams & {
  id: string;
  created_at: string;
  updated_at: string;
  links: BridgeUserLink[] | null;
  /**
   * GSE accepted income and employment verification report ID
   */
  voie_report_id: string | null;
  /**
   * Verification of Assets report ID
   */
  voa_report_id: string | null;
  /**
   * AIM check report ID
   */
  aim_check_report_id: string | null;
  /**
   * Income Insights report ID
   */
  income_insights_report_id: string | null;
};

export type BridgeUserLink = {
  id: string;
  created_at: string;
  updated_at: string;
  tracking_info?: string;
  status: ValueOf<typeof TaskStatusMap>;
  user_external_id?: string;
  provider_id?: string;
  provider?: ProviderBase;
  company_mapping?: ProviderBase;
  link_hash?: string;
  allowed_products?: VerificationType[];
  initial_product_type: VerificationType;
  data_source?: DataSource;
  is_suspicious?: boolean;
  deleted_at?: string | null;
};

export type GetBridgeUsersParams = Partial<PagingFilters> &
  BaseFilters & {
    query?: string;
    product_type?: string;
    provider?: string;
    list_links?: boolean;
    link_exists?: boolean | null;
  };

export type GetBridgeUsersResponse = ListResult<BridgeUserResponse>;

export type UsageReportsParams = {
  year: string;
} & Partial<PagingFilters>;

export type UsageReportsResponse = ListResult<{
  created_at: string;
  id: string;
  invoice_id: string;
  period_end: string;
  period_start: string;
  report: string | null;
}>;

export type GetTwilioSettings = {
  twilio_account_sid: string;
  twilio_phone_number: string;
  twilio_messaging_service_sid?: string;
};

export type UpdateTwilioSettings = GetTwilioSettings & {
  twilio_auth_token?: string; // write-only field
};

export type UploadedDocumentInfo = {
  id: string;
  type: string;
  subtype: string;
  filename: string;
  file: string;
  created_at: string;
  updated_at: string;
};

export type ScoringAttributesFileResponse = {
  url: string;
};

export type RequestCoverageParams = {
  product_type: VerificationType;
  company_name: string;
  company_website?: string;
  provider_name?: string;
  comment?: string;
};

export type SubcompaniesResponse = {
  id: string;
  name: string;
  legal_name: string;
  logo: string;
  owner_email: string;
}[];

export type AnalyticsSourceFilters = {
  period_start?: string;
  period_end?: string;
  product_types?: string[];
  // Single-value `template` is still used by the Team Performance filters; the
  // reporting page uses the multi-value `templates`.
  template?: string;
  templates?: string[];
  data_sources?: string[];
  companies?: string;
  goal?: number;
  sources?: string[];
  is_refresh?: string;
};

export type AnalyticsEmbeddedTotalsResponse = {
  users: number;
  users_trend: number | null;
  completed: number;
  completed_trend: number | null;
  cr: number | null;
  cr_trend: number | null;
}[];

export type AnalyticsOrdersTotalsResponse = {
  orders: number;
  completed: number;
  canceled_orders: number;
  email_open_rate: number | null;
  email_cr: number | null;
  sms_cr: number | null;
  cr: number | null;
  canceled_orders_trend: number | null;
  orders_trend: number | null;
  completed_trend: number | null;
  cr_trend: number | null;
  time_to_complete: number | null;
}[];

export type AnalyticsEmbeddedTrendResponse = {
  date: string;
  preceding_period_cr: number | null;
  preceding_period_users: number;
  selected_period_completed: number;
  selected_period_cr: number | null;
  selected_period_users: number;
}[];

export type AnalyticsOrdersTrendResponse = {
  date: string;
  selected_period_orders: number;
  selected_period_completed: number;
  selected_period_cr: number | null;
  preceding_period_orders: number;
  preceding_period_completed: number;
  preceding_period_cr: number | null;
}[];

export type AnalyticsEmbeddedFunnelResponse = {
  status_id: number;
  users: number;
  drop_off: number;
  cr: number | null;
  cr_from_previous_step: number | null;
}[];

export type AnalyticsOrdersFunnelResponse = {
  status_id: number;
  orders: number;
  drop_off: number;
  cr: number | null;
  cr_from_previous_step: number | null;
}[];

export type AnalyticsOrdersStatusDistributionResponse = {
  status: 'expired' | 'completed' | 'skipped' | 'canceled' | 'no_data' | 'error' | 'pending' | 'sent';
  orders: number;
}[];

// One row per (error bucket, top-5 message). `error` is backend-driven and the
// set drifts across flows, so it's typed as a string rather than a union.
// `status_total` is the true bucket total across all messages (not just the
// top-5), so percentages are `users / status_total`.
export type AnalyticsErrorsDistributionResponse = {
  error: string;
  error_message: string;
  users: number;
  status_total: number;
}[];

// Pending orders bucketed by time remaining until expiry (orders flow only).
// Buckets with zero orders are omitted, so the FE fills the fixed cohort set.
export type AnalyticsOrdersTimeToExpireResponse = {
  expiry_bucket: string;
  orders: number;
}[];

export type AnalyticsTATMedianResponse = {
  median_tat: number | null;
}[];

export type AnalyticsTATTotalsResponse = {
  created: number;
  completed: number;
}[];

export type AnalyticsTATHistogramResponse = {
  hours_cat: string;
  total_orders: number;
}[];

export type AnalyticsBenchmarksResponse = {
  industry: string;
  users: number;
  cr: number | null;
  benchmark_cr: number | null;
}[];

type AnalyticsTemplateBase = {
  template_id: string | null;
  template_name: string | null;
  is_deleted: boolean;
  is_refresh: boolean;
  cr: number | null;
};

export type AnalyticsOrderTemplatesResponse = (AnalyticsTemplateBase & {
  orders: number;
  completed_orders: number;
  bridge_opened_orders: number;
  cr_from_bridge_opened: number | null;
})[];

export type AnalyticsEmbeddedTemplatesResponse = (AnalyticsTemplateBase & {
  users: number;
  done_users: number;
})[];

export type AnalyticsTeamPerformanceTableResponse = {
  completed_orders: number;
  completion_rate: number;
  created_orders: number;
  manager_email: string | null;
  manager_name: string | null;
}[];

export type AnalyticsTeamPerformanceTotalsResponse = {
  active: number;
  inactive: number;
  pending: number;
  total: number;
}[];

export type CreateReportResponse = {
  report_id: string;
};

export type ScoringAttributesReportResponse = {
  status: 'processing' | 'success';
  [key: string]: unknown;
};

export type PlatformKeysBannoParams = {
  client_id: string | null;
  client_secret: string | null;
  environment: string | null;
};

export type PlatformKeysBannoResponse = PlatformKeysBannoParams & {
  plugin_url_static: string | null;
  plugin_url_dynamic: string | null;
  id: string;
};

export type PlatformKeysBannoError = {
  [k in keyof PlatformKeysBannoParams]?: string[];
};

export type PlatformKeysBlackKnightParams = {
  party_id: string;
};

export type PlatformKeysBlackKnightResponse = PlatformKeysBlackKnightParams & {
  id: string;
};

export type PlatformKeysBlackKnightError = {
  [k in keyof PlatformKeysBlackKnightParams]?: string[];
};

export type PlatformKeysSimpleNexusParams = {
  api_key: string | null;
};

export type PlatformKeysSimpleNexusResponse = PlatformKeysSimpleNexusParams & {
  id: string;
  core_user_key_env: EnvType;
};

export type PlatformKeysSimpleNexusError = {
  [k in keyof PlatformKeysSimpleNexusParams]?: string[];
};

export type CompanySearchPublicResponse = TruvSuggestionType[];

export type BatchOrdersRequest = {
  products: VerificationType[];
  template_id?: string;
  file: File;
  env_type: EnvType;
};

export type BatchOrdersResponse = {
  created_at: string;
  data: BatchOrderData[];
  errors: ({
    [key in keyof BatchOrderData]?: string[];
  } & {
    employers?: {
      account?: {
        [key in keyof BankAccount]?: string[];
      };
    }[];
  } & {
    lineno: number;
  })[];
  id: string;
  order_groups:
    | {
        id: string;
        first_name: string;
        last_name: string;
        share_url: string;
        email: string | null;
        phone_number: string | null;
      }[]
    | null;
  status: 'pending' | 'prepared' | 'completed' | 'failed';
  updated_at: string;
};

export type BatchOrderData = {
  first_name: string;
  last_name: string;
  email: string;
  phone_number: string;
  ssn: string | null;
  employer_names: string[];
  tracking_number: string | null;
  loan_number: string | null;
  loan_originator_name: string | null;
  template_id?: string;
  custom_field: string | null;
  bank_name: string | null;
  bank_address: string | null;
  routing_number: string | null;
  account_number: string | null;
  account_type: string | null;
  deposit_type: string | null;
  deposit_value: string | null;
};

export type BatchOrdersError =
  | string[]
  | {
      [key in keyof BatchOrdersRequest]?: string[] | BatchOrdersError;
    };

export type PerformanceReportsParams = Partial<PagingFilters> & {
  year?: string;
  use_case?: string;
};

export type PerformanceReportsResponse = ListResult<{
  id: string;
  user_id: string;
  products: StrictOrderProductType;
  use_case: string;
  report_date: string;
  report: string;
  created_at: string;
  updated_at: string;
}> & {
  use_cases: UseCase[];
};

export enum UseCase {
  BACKGROUND_SCREENING = 'background_screening',
  MORTGAGE_LENDING = 'mortgage_lending',
  HOME_EQUITY_LENDING = 'home_equity_lending',
  CONSUMER_LENDING = 'consumer_lending',
  AUTO_LENDING = 'auto_lending',
  TENANT_SCREENING = 'tenant_screening',
  BANKING = 'banking',
  GOVERNMENT = 'government',
}

export const VERIFICATION_VOLUMES = [
  'mortgage_lt_1000',
  'mortgage_1000_3000',
  'mortgage_3000_10000',
  'mortgage_10000_plus',
  'verif_lt_10000',
  'verif_10000_50000',
  'verif_50000_250000',
  'verif_250000_plus',
] as const;

export type VerificationVolume = (typeof VERIFICATION_VOLUMES)[number];

export type BridgeToken = {
  bridge_token: string;
  tracking_info?: string;
  client_name?: string;
  product_type?: VerificationType;
  allowed_products?: VerificationType[];
  company_mapping_id?: string;
  access_token?: string;
  user_id?: string;
};

export type BridgeTokenSettings = {
  product_type: VerificationType;
  tracking_info?: string;
  allowed_products?: VerificationType[];
  company_mapping_id?: string;
  access_token?: string;
  provider_id?: string;
  template_id?: string;
  account?: BankAccount;
  data_sources?: DataSource[];
  data_source_flow?: DataSourceFlow;
};

export type BridgeTokenParams = {
  type: VerificationType;
  mappingId?: string;
  providerId?: string;
  template?: string;
  account?: BankAccount;
  dataSources?: string;
  dataSourceFlow?: DataSourceFlow;
};

export type DataSourcesParams = {
  product_type?: VerificationType;
  template_id?: string;
  use_case?: UseCase;
};

export type DataSourcesResponse = Record<VerificationType, DataSource[]>;

export type UserProperties = {
  isDevRequestSubmitted?: boolean;
  isProductionRequestSubmitted?: boolean;
  isCustomizationRequestSent?: boolean;
  isSandboxMode?: boolean;
  lastOrderProductType?: VerificationType[];
  lastEmulatorProductType?: ProductType;
  targetVisited?: boolean;
};

export type WebhookItem = {
  id: string;
  name: string;
  webhook_url: string;
  events: string[];
  env_type: EnvType;
  enabled: boolean;
  created_at: string;
  updated_at: string;
};

export type CreateWebhookParams = Omit<WebhookItem, 'id' | 'created_at' | 'updated_at'>;

export type UpdateWebhookParams = {
  id: string;
  payload: Partial<CreateWebhookParams>;
};

export type UpdateCompanyRequest = Partial<Omit<CompanyResponse, 'client_id' | 'has_prod_usage'>>;

export type OrbSubscriptionResponse = {
  subscriptions: OrbSubscription[];
};

export type SubscriptionStatus = 'active' | 'scheduled_cancel' | 'ended';

export type SubscriptionDetailResponse = {
  id: string;
  plan_slug: string;
  status: SubscriptionStatus;
  current_period_end: string | null;
  scheduled_cancel_at: string | null;
  cancel_reason: string | null;
};

export type CancelSubscriptionRequest = {
  reason?: string;
};

/** The plans the FE may pick via change-plan. Excludes ``one_off`` (PAYG, no upgrade target). */
export type CustomerSelectablePlanId = 'launch' | 'growth' | 'professional' | 'professional_plus';

export type ChangePlanRequest = {
  target_slug: CustomerSelectablePlanId;
};

export type ChangePlanResponse = {
  summary: SubscriptionDetailResponse;
  previous_subscription_id: string | null;
  /** Plan slug before this call. ``null`` or ``one_off`` indicates the change is also a
   * conversion that flips Company.company_type from INDIVIDUAL to SELF_SERVE. */
  previous_plan_slug: string | null;
};

export type OrbSubscription = {
  start_date: string;
  end_date: string | null;
  net_terms: number;
  active_products: OrbActiveProduct[];
  inactive_products: OrbProduct[];
};

export type OrbActiveProduct = OrbProduct &
  {
    [K in keyof OrbPriceConfigByModelType]: {
      price_model_type: K;
    } & OrbPriceConfigByModelType[K];
  }[keyof OrbPriceConfigByModelType];

export type OrbProduct = {
  name: string;
  cadence: 'monthly' | 'quarterly' | 'annual' | 'one_time';
  price_type: 'usage_price' | 'fixed_price';
};

type OrbPriceConfigByModelType = {
  unit: {
    unit_config: {
      unit_amount: string;
    };
  };
  tiered: {
    tiered_config: {
      tiers: OrbPriceTier[];
    };
  };
};

type OrbPriceTier = {
  first_unit: number;
  last_unit?: number | null;
  unit_amount: string;
};

export type OrbInvoiceParams = {
  datetime_gte: string; // It is optional, but we will always provide it
  datetime_lt: string; // It is optional, but we will always provide it
  page_size: number; // It is optional, but we will always provide it
  cursor?: string;
};

export type OrbInvoiceResponse = {
  invoices: OrbInvoice[];
  cursor: string | null;
};

export type OrbInvoice = {
  invoice_date: string;
  amount: string;
  invoice_number: string;
  due_date: string;
  status: 'issued' | 'paid'; // Backend promises not to return 'draft' | 'void' | 'uncollectible', we will send to sentry if it happens
  invoice_link: string | null;
  invoice_pdf: string;
};

export type OrbUsageParams = {
  datetime_gte: string;
  datetime_lt: string;
  cursor?: string;
  limit: number;
};

export type OrbUsageResponse = {
  cursor: string | null;
  results: UsageReportItem[];
};

export type UsageReportItem = {
  type: 'report' | 'task' | 'loan';
  created_at: string;
  completed_at: string | null;
  bridge_token_id: string | null;
  product_type: VerificationType | null;
  data_source: string | null;
  use_case: string | null;
  task_id: string | null;
  /**
   * @deprecated This field is deprecated and will be removed in a future release.
   */
  client_id: string | null;
  /**
   * @deprecated This field is deprecated and will be removed in a future release.
   */
  client_email: string | null;
  order_group_id: string | null;
  provider_name: string | null;
  template_id: string | null;
  template_name: string | null;
  status: string | null;
  is_refresh: boolean | null;
  funding_date: string | null;
  tracking_number: string | null;
  loan_number: string | null;
  loan_originator_name: string | null;
  /**
   * @deprecated This field is deprecated and will be removed in a future release.
   */
  owner_email: string | null;
  platform: string | null;
  order_group_manager_name: string | null;
  order_group_manager_email: string | null;
  borrower_name: string | null;
  borrower_email: string | null;
  price: string | null;
  report_id: string | null;
};

export type OrbCreditLedgerParams = {
  datetime_gte: string;
  datetime_lt: string;
};

export type OrbCreditLedgerResponse = {
  daily: DailyLedgerAggregation[];
  total: DailyLedgerAggregation;
};

type DailyLedgerAggregation = {
  created_at: string;
  starting_balance: string;
  ending_balance: string;
  increment: AmountAggregation | null;
  decrement: AmountAggregation | null;
  credit_block_expiry: AmountAggregation | null;
  void: AmountAggregation | null;
  amendment: AmountAggregation | null;
};

type AmountAggregation = {
  amount: string;
};

export type Permission =
  | 'can_use_impersonation'
  | 'can_view_analytics'
  | 'can_view_billing'
  | 'can_view_billing_stats'
  | 'can_view_settings'
  | 'can_view_tasks'
  | 'can_view_keys'
  | 'can_view_provider_status'
  | 'can_view_logs'
  | 'can_view_orders'
  | 'can_view_team_performance'
  | 'can_view_customization'
  | 'can_edit_customization'
  | 'can_view_profile'
  | 'can_view_company_settings'
  | 'can_edit_company_settings'
  | 'can_view_webhooks'
  | 'can_invite_users'
  | 'can_view_development'
  | 'can_view_reports'
  | 'can_view_profiles'
  | 'can_edit_custom_domain'
  | 'can_view_support_tickets'
  | 'can_view_embedded'
  | 'can_view_client_dashboard'
  | 'can_view_platform_keys'
  | 'can_edit_banno_settings'
  | 'can_edit_blackknight_settings'
  | 'can_edit_simplenexus_settings'
  | 'can_download_scoring_attributes_file'
  | 'can_view_performance_reports'
  | 'can_view_sandbox'
  | 'can_edit_billing';

export type Permissions = Set<Permission>;

export type BillingAddress = {
  line1?: string;
  city?: string;
  state?: string;
  postal_code?: string;
};

type BillingBrand =
  | 'amex'
  | 'diners'
  | 'discover'
  | 'eftpos_au'
  | 'jcb'
  | 'link'
  | 'mastercard'
  | 'unionpay'
  | 'visa'
  | 'unknown';

export type PaymentCard = {
  brand: BillingBrand;
  last4: string;
  exp_month: number;
  exp_year: number;
};

/**
 * The unresolved payment failure blocking a user's charges, from `GET /billing/profile/`.
 *
 * Null on `BillingProfileResponse` when there is no failure to report. Present independently of
 * `payment_status`: the failure is read from the payment records, while `payment_status` also
 * reflects subscription and payment-method health, so `'processing'` can arrive alongside a
 * failure the backend is already retrying — render the processing state, not the failure.
 *
 * - `code` / `decline_code` are null when the provider reported a failure without a reason. The
 *   reason is resolved asynchronously after a charge fails, so both can be null briefly.
 * - `message` is already mapped to customer-facing copy and is never empty, so it can be
 *   rendered as-is without a local fallback.
 * - `last_attempt_at` is an ISO 8601 timestamp of the last collection attempt.
 * - `reports_locked` mirrors the backend's own PDF-report gate rather than being inferred, so it
 *   is safe to assert reports are withheld.
 */
export type PaymentFailure = {
  status: PaymentStatus;
  code: string | null;
  decline_code: string | null;
  message: string;
  last_attempt_at: string;
  failed_order_count: number;
  reports_locked: boolean;
};

export type CustomerData = {
  name?: string;
  email?: string;
  billing_contact?: string;
  support_email?: string;
  company_name?: string;
  billing_address?: BillingAddress;
};

export type SubscriptionPlanId = 'one_off' | 'launch' | 'growth' | 'professional' | 'professional_plus';

export type BillingProfileResponse = {
  is_corporate: boolean;
  is_orb_customer: boolean;
  subscription_id: SubscriptionPlanId | null;
  subscription_status?: 'in_progress' | 'active' | null;
  payment_status?: 'active' | 'error' | 'processing' | null;
  payment_failure?: PaymentFailure | null;
  can_subscribe: boolean;
  has_credits: boolean;
  has_pending_credits?: boolean;
  default_payment_card?: PaymentCard;
  customer_data: CustomerData | null;
};

export type UpdateBillingProfileRequest = {
  customer_data?: CustomerData;
  setup_intent_id?: string;
  subscription_id?: string;
};

export type KycDetailsRequest = {
  website_url: string;
  reason: string;
  company_name: string;
  state: string;
};

export enum PricingSlug {
  VOIE_REPORTS = 'voie_reports',
  VOA_DVOE_REPORTS = 'voa_dvoe_reports',
  AIM_CHECK_REPORTS = 'aim_check_reports',
  VOIE_CONNECTION = 'voie_connection',
  VOA_DVOE_CONNECTION = 'voa_dvoe_connection',
  DOCS_VERIFICATION = 'docs_verification',
  REVERIFICATIONS = 'reverifications',
  INSURANCE_VERIFICATION = 'insurance_verification',
  MANUAL_ORDER_FEE = 'manual_order_fee',
}

export type BillingPlanPrice = {
  slug: PricingSlug;
  name: string;
  amount: string;
};

export type BillingPlan = {
  plan: string;
  credits?: number;
  cadence?: 'monthly' | 'yearly';
  prices: BillingPlanPrice[];
};

export type BillingPlansByUseCase = {
  use_case: UseCase;
  plans: BillingPlan[];
};

export type BillingPlansResponse = BillingPlansByUseCase[];

export type SetupIntentResponse = {
  client_secret: string;
  setup_intent_id: string;
};

export type BankIncomeReport = {
  status: 'success' | 'processing';
  completed_at: string | null;
  days_requested: number;
  tracking_info: string | null;
  provider: string;
  access_token: string | null;
  companies: string[] | null;
  accounts: BankIncomeAccount[];
  income: BankIncomeSource[];
  summary: BankIncomeSummary;
  pdf_report?: string;
  is_suspicious: boolean | null;
};

export type BankIncomeAccount = AccountBase & {
  days_available: number;
};

export type BankIncomeSource = {
  start_date: string;
  end_date: string;
  account_id: string;
  income_description: string;
  income_category: IncomeCategory;
  pay_frequency: PayFrequencyType | null;
  next_payment_date: string | null;
  total_amount: string;
  iso_currency_code: string;
  transaction_count: number;
  historical_summary: BankIncomeSourceHistoricalSummary[];
  historical_average_monthly_income: string;
  historical_average_monthly_gross_income: string;
  forecasted_average_monthly_income: string;
  avg_deposit_amount: string;
  avg_gross_deposit_amount: string;
};

export type BankIncomeSourceHistoricalSummary = BankIncomeInsightsSourceHistoricalSummary & {
  avg_deposit_amount: string | null;
  avg_gross_deposit_amount: string | null;
};

export type BankIncomeSourceHistoricalSummaryTransaction = {
  amount: string | null;
  iso_currency_code: string | null;
  date: string | null;
  check_number: string | null;
  description: string | null;
  pending: boolean | null;
  transaction_id: string | null;
};

export type BankIncomeSummary = {
  start_date: string;
  end_date: string;
  income_sources_count: number;
  income_categories_count: number;
  income_transactions_count: number;
  total_amount: string;
  iso_currency_code: string;
  historical_average_monthly_gross_income: string;
  historical_average_monthly_income: string;
  forecasted_average_monthly_income: string;
  historical_annual_gross_income: string;
  historical_annual_income: string;
  forecasted_annual_income: string;
};

export type UserReportedIncomeDeposit = {
  amount: string | null;
  iso_currency_code: string | null;
  date: string | null;
  description: string | null;
};

export type UserReportedIncomeSource = {
  id: string;
  income_description: string | null;
  total_amount: string | null;
  iso_currency_code: string | null;
  transaction_count: number | null;
  historical_summary: { transactions: UserReportedIncomeDeposit[] }[] | null;
  certification: SelfCertificationResult & {
    certified_data?: {
      type?: string | null;
      monthly_income?: string | null;
      hours_per_month?: string | null;
    } | null;
  };
};

export const PAY_FREQUENCY = {
  M: 'Monthly',
  SM: 'Semi-Monthly',
  W: 'Weekly',
  BW: 'Bi-Weekly',
  A: 'Annually',
  SA: 'Semi-Annually',
  C: 'Commission',
} as const;

export type PayFrequencyType = keyof typeof PAY_FREQUENCY;

export type IncomeInsightsRequest = {
  days_requested: number;
  consumer_report_permissible_purpose: ConsumerReportPermissiblePurpose;
};

export type ConsumerReportPermissiblePurpose =
  | 'ACCOUNT_REVIEW_CREDIT'
  | 'ACCOUNT_REVIEW_NON_CREDIT'
  | 'EMPLOYMENT'
  | 'EXTENSION_OF_CREDIT'
  | 'LEGITIMATE_BUSINESS_NEED_TENANT_SCREENING'
  | 'LEGITIMATE_BUSINESS_NEED_OTHER'
  | 'WRITTEN_INSTRUCTION_PREQUALIFICATION'
  | 'WRITTEN_INSTRUCTION_OTHER';

export type IncomeInsightsApplicantReport = {
  report_id: string;
  created_at: string;
  completed_at: string | null;
  days_requested: number;
  links: LinkIncomeInsights[];
  bank_income_summary: BankIncomeInsightsSourcesSummary;
};

type LinkIncomeInsights = {
  link_id: string;
  created_at: string;
  updated_at: string;
  provider: string;
  provider_name: string;
  tracking_info: string | null;
  accounts: AccountIncomeInsights[];
  bank_income_sources: BankIncomeInsightsSource[] | null;
};

type BankIncomeInsightsSourcesSummary = {
  start_date: string;
  end_date: string;
  income_sources_count: number;
  income_categories_count: number;
  income_transactions_count: number;
  total_amounts: AmountISOCurrency[];
  historical_average_monthly_gross_income: AmountISOCurrency[];
  historical_average_monthly_income: AmountISOCurrency[];
  forecasted_average_monthly_income: AmountISOCurrency[];
  historical_annual_gross_income: AmountISOCurrency[];
  historical_annual_income: AmountISOCurrency[];
  forecasted_annual_income: AmountISOCurrency[];
};

type AmountISOCurrency = {
  amount: string;
  iso_currency_code: string;
};

export type AccountIncomeInsights = {
  id: string;
  mask: string | null;
  nickname: string | null;
  type: string | null;
  subtype: string | null;
  days_available: number;
  owners: AccountOwner[];
};

export type BankIncomeInsightsSource = {
  start_date: string;
  end_date: string;
  account_id: string;
  income_description: string;
  income_category: IncomeCategory;
  pay_frequency: PayFrequencyType | null;
  next_payment_date: string | null;
  total_amount: string;
  iso_currency_code: string;
  transaction_count: number;
  historical_summary: BankIncomeInsightsSourceHistoricalSummary[];
  historical_average_monthly_income: string | null;
  historical_average_monthly_gross_income: string | null;
  forecasted_average_monthly_income: string | null;
  avg_deposit_amount: string;
  avg_gross_deposit_amount: string;
  employer: ProviderBase | null;
  // present when the order-group response is fetched with certifications (bank_income self-cert)
  certification?: SelfCertificationResult | null;
};

type BankIncomeInsightsSourceHistoricalSummary = {
  start_date: string;
  end_date: string;
  total_amount: string;
  iso_currency_code: string;
  transactions: BankIncomeSourceHistoricalSummaryTransaction[];
};

export type StatementsListParams = Partial<PagingFilters> & {
  pay_date_from?: string;
  pay_date_to?: string;
  employment_id?: string;
  return_all_statements?: boolean;
};

export type StatementsListResponse = ListResult<Statement>;

export type BankStatement = {
  id: string;
  // Statement id assigned by the provider.
  external_id: string;
  statement_date: string;
  account_id: string;
  file: string;
};

export type BankStatementsListResponse = ListResult<BankStatement>;

export type UnresolvedIncident = {
  id: string;
  name: string;
  status: string;
  impact: string;
  page_id: string;
  shortlink: string;
  created_at: string;
  scheduled_for?: string | null;
  scheduled_until?: string | null;
};

export type UnresolvedIncidentResponse = UnresolvedIncident[];

export type UnresolvedIncidentsParams = {
  per_page?: number;
  page?: number;
};
